<template>
  <div class="col-md-5 !tw-mx-auto login-wrapper login__main">
    <div class="login-logo">
      <a href="/">
        <img
          :src="logo"
          alt="logo"
        >
      </a>
    </div>

    <form
      :class="{ 'loading':loading }"
      @submit.prevent
    >
      <Card class="card-login">
        <div>
          <UiInput
            v-model="model.email"
            v-model:error="errors.email"
            :label="$t('forms.emailLabel')"
            :placeholder="$t('forms.emailPlaceholder')"
            class="mb-16"
            autofocus
            wrapper-type="registration"
            error-clear-on-change
          />
          <UiInput
            v-model="model.password"
            v-model:error="errors.password"
            class="mb-12"
            :label="$t('forms.passwordLabel')"
            type="password"
            placeholder="********"
            wrapper-type="registration"
            error-clear-on-change
          />
        </div>
        <div
          v-show="isCaptchaV2Required"
          class="ui-m-lg-b"
        >
          <VueRecaptcha
            ref="captchaV2"
            :sitekey="$store.state.misc.miscSettings.recaptcha.public_key_v2"
            @verify="verifyV2"
          />
        </div>
        <div class="actions ui-m-lg-b ui-align-center">
          <div class="remember_me_checkbox">
            <div class="checkbox check">
              <input
                id="checklog"
                v-model="model.remember"
                type="checkbox"
              >
              <label
                class="checkbox-label remText"
                for="checklog"
              >
                {{ $t('forms.remember_me') }}
              </label>
            </div>
          </div>
          <RouterLink
            :to="{ path: '/restore-password'}"
            replace
          >
            {{ $t('forms.forgot_password') }}
          </RouterLink>
        </div>

        <div class="button__login">
          <button
            :disabled="!valid_password || !valid_email || (isCaptchaV2Required && !recaptchaTokenV2) || loading"
            class="btn btn-primary btn-block big login btn-md"
            @click="login"
          >
            {{ $t('forms.btn.login') }} >
          </button>
          <div class="!tw-text-center link_sign-up tw-mt-8">
            <span>{{ $t('forms.notAccount') }}</span>
            <RouterLink
              class="ml-8"
              to="/registration"
            >
              {{ $t('forms.signUp') }}
            </RouterLink>
          </div>
        </div>
      </Card>

      <div class="loading__block">
        <i class="fa fa-spinner fa-3x fa-fw login__spinner" />
      </div>

      <div class="error__block">
        <i class="far fa-times-circle" />
        <h2 class="login__error-title">
          {{ $t($store.state.errors.message) }}
        </h2>
        <button
          class="btn btn-primary btn-block big login btn-md"
          @click="error = false"
        >
          &lt; {{ $t('forms.btn.back') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import { required, email, minLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import eventBus from '@/lib/eventBus';

export default {
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      passwordShown: false,
      passwordValidation: false,
      emailValidation: false,
      isCaptchaV2Required: false,
      recaptchaTokenV2: null,
      recaptchaTokenV3: null,
      loading: false,
      count: 0,
      errors: {},
      model: {
        email: localStorage.getItem('email') || '',
        password: '',
        remember: true,
      },
    };
  },
  validations: {
    model: {
      password: {
        required,
        minLength: minLength(8),
      },
      email: {
        required,
        email,
      },
    },
  },
  computed: {
    valid_email() {
      const _email = this.v$.model.email;
      return _email.email && _email.required;
    },
    valid_password() {
      const _password = this.v$.model.password;
      return _password.minLength && _password.required;
    },
    logo() {
      return this.$config.layout.logoFull;
    },
  },

  mounted() {
    const routeName = this.$route.name || '';
    document.title = `${this.$t(routeName)} - ${this.$config.pageTitle}`;
    eventBus.$on('otp-dialogue', () => {
      this.$router.push('/second-factor');
    });
  },

  watch: {
    'model.email': function modelEmail() {
      localStorage.setItem('email', this.model.email);
    },
  },

  methods: {
    verifyV2(token) {
      this.recaptchaTokenV2 = `google_recaptcha_v2__${token}`;
    },
    async recaptcha() {
      try {
        await this.$recaptchaLoaded();
        const token = await this.$recaptcha('login');
        this.recaptchaTokenV3 = `google_recaptcha_v3__${token}`;

      } catch (error) {
        console.log(error);
      }
    },
    async login() {
      if (this.invalid_form) return;
      this.loading = true;

      const recaptcha_token = this.recaptchaTokenV2 || this.recaptchaTokenV3;

      await this.$store.dispatch('login', {
        ...this.model,
        ...(recaptcha_token && { recaptcha_token }),
      })
        .then(() => {
          setTimeout(async () => {
            if (this.$route.name !== 'routes.2fa') {
              try {
                const profileData = await this.$store.dispatch('getProfileData');
                /**
                 * setting language from profile
                 */
                localStorage.setItem('language', profileData.language_code);
                this.$locale.value = profileData.language_code;
                await this.$store.dispatch('getSettingsLazy');
                this.$router.push(this.$config.mainScreen);
              } catch (error) {
                if (error?.response?.data?.code?.startsWith('AFFILIATE_KYC')) {
                  this.$router.push(this.$config.mainScreen);
                }
              }
            }
          }, 1000);
        })
        .catch(async (err) => {
          this.errors = err.response.data?.errors || [];
          if (this.errors.hasOwnProperty('recaptcha_token')) {
            if (!this.recaptchaTokenV3) {
              await this.recaptcha();
              return await this.login();
            }

            if (!this.recaptchaTokenV2) {
              this.isCaptchaV2Required = true;
              this.loading = false;
              return;
            }

            this.$alert.error(this.$t('alerts.captchaRequestFailed'));
            this.loading = false;
            return;
          }

          if (err.response && err.response.data.code === 'AUTH_INVALID_CREDENTIALS_ERROR') {
            this.$router.push('/wrong-credentials');
          }
          if (err.response && err.response.data.message) {
            this.$alert.error(err.response.data.message);
          }
          this.loading = false;
        })
        .finally(() => {
          this.$refs.captchaV2?.reset();
          this.recaptchaTokenV2 = null;
        });
    },
    showPassword() {
      this.passwordShown = !this.passwordShown;
    },
  },
};
</script>

<style lang="scss">
@import 'styles/login';
</style>
